import { useEffect, useState } from "react";
import { Brain, ChevronRight, LoaderCircle } from "lucide-react";
import {
  messageDisplaySummary,
  type Message,
  type ToolEvent,
} from "@lumen/shared";
import { api } from "../../api";
import { t } from "../../i18n";
import { SubagentDiscussion } from "../agent-workspaces/SubagentDiscussion";
import { Markdown } from "../Markdown";
import { mergeAssistantFragments } from "../../pages/chat/compactionTimeline";
import { MessageActivity, type MessageActivityProps } from "./MessageActivity";

export function CompletedMessageActivity(props: MessageActivityProps) {
  const { message } = props;
  const summary = messageDisplaySummary(message);
  const [open, setOpen] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [loaded, setLoaded] = useState<{
    key: string;
    message?: Message;
    error?: string;
  } | null>(null);
  const ids = message.activityMessageIds ?? [message.id];
  const key = `${message.conversationId}:${ids.join(",")}:${message.content?.length ?? 0}:${message.generationStatus}`;
  const idsKey = ids.join(",");
  const loadedMessageKey = loaded?.message ? loaded.key : null;
  useEffect(() => {
    if (!open || !message.activityDeferred || loadedMessageKey === key) return;
    const controller = new AbortController();
    void Promise.all(
      idsKey
        .split(",")
        .map((id) =>
          api<Message>(
            `/conversations/${message.conversationId}/messages/${id}/activity`,
            { signal: controller.signal },
          ),
        ),
    )
      .then((fragments) => {
        if (!controller.signal.aborted)
          setLoaded({
            key,
            message: mergeAssistantFragments(
              fragments,
              (fragment) =>
                fragments[fragments.indexOf(fragment) - 1]?.id ?? null,
            ).at(-1),
          });
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted)
          setLoaded({
            key,
            error:
              error instanceof Error
                ? error.message
                : "Could not load activity",
          });
      });
    return () => controller.abort();
  }, [
    open,
    key,
    idsKey,
    message.conversationId,
    message.activityDeferred,
    attempt,
    loadedMessageKey,
  ]);
  const details = message.activityDeferred
    ? loaded?.key === key
      ? loaded.message
      : undefined
    : message;
  const error = loaded?.key === key ? loaded.error : undefined;
  const finalEventId = details
    ? messageDisplaySummary(details).finalTextEventId
    : null;
  const visibleRunIds = new Set(message.subagentRuns?.map((run) => run.id));
  const isVisibleDelegation = (event: ToolEvent) => {
    // Preserve malformed activity for the diagnostic renderer below.
    if (!event || typeof event.name !== "string") return false;
    const result = event.result;
    return Boolean(
      event.name.startsWith("delegate_") &&
      result &&
      typeof result === "object" &&
      "subagentRunId" in result &&
      typeof result.subagentRunId === "string" &&
      visibleRunIds.has(result.subagentRunId),
    );
  };
  const timeline = details
    ? {
        ...details,
        subagentRuns: details.subagentRuns?.filter(
          (run) => !visibleRunIds.has(run.id),
        ),
        toolEvents: (details.toolEvents ?? []).filter(
          (event) => !isVisibleDelegation(event),
        ),
        activityEvents: (details.activityEvents ?? []).filter(
          (event) =>
            event.id !== finalEventId &&
            !(event.type === "tool" && isVisibleDelegation(event.tool)),
        ),
      }
    : undefined;
  return (
    <>
      {summary.hasActivity && (
        <details
          open={open}
          onToggle={(event) => setOpen(event.currentTarget.open)}
          className="mb-3 rounded-xl border border-stone-200 bg-stone-50/70 text-xs dark:border-white/10 dark:bg-white/[0.03]"
        >
          <summary className="flex min-h-11 cursor-pointer items-center gap-2 px-3 py-2 font-semibold text-stone-500">
            <Brain size={14} aria-hidden="true" />
            {t("Model activity")}
            <ChevronRight
              size={14}
              aria-hidden="true"
              className={`ml-auto motion-safe:transition-transform ${open ? "rotate-90" : ""}`}
            />
          </summary>
          {open && (
            <div
              className="space-y-3 border-t border-stone-200 p-3 dark:border-white/10"
              aria-busy={!details && !error}
            >
              {error ? (
                <div role="alert">
                  {error}
                  <button
                    type="button"
                    className="button ml-2 min-h-11 px-3"
                    onClick={() => {
                      setLoaded(null);
                      setAttempt((value) => value + 1);
                    }}
                  >
                    {t("Retry")}
                  </button>
                </div>
              ) : timeline ? (
                <MessageActivity {...props} message={timeline} busy={false} />
              ) : (
                <div role="status" className="flex items-center gap-2">
                  <LoaderCircle
                    size={14}
                    className="animate-spin"
                    aria-hidden="true"
                  />
                  {t("Loading activity…")}
                </div>
              )}
            </div>
          )}
        </details>
      )}
      {message.subagentRuns?.map((run) => (
        <div key={run.id} className="mb-3">
          <SubagentDiscussion
            run={run}
            approvals={props.approvals ?? new Map()}
            busy={props.busy ?? false}
            decidingApprovalIds={props.decidingApprovalIds ?? new Set()}
            onApproval={props.onApproval}
            onQuestionAnswer={props.onQuestionAnswer}
            onSubagentContinue={
              props.onSubagentContinue &&
              !summary.finalContent &&
              run.id === message.subagentRuns?.at(-1)?.id
                ? () => props.onSubagentContinue!(run)
                : undefined
            }
          />
        </div>
      ))}
      {summary.finalContent && <Markdown>{summary.finalContent}</Markdown>}
    </>
  );
}
